/**
 * @file PickingTaskNotices.tsx
 * @description Полосы-предупреждения задания на сборку (phase19.23).
 *
 * Собраны в один компонент, чтобы страница не превращалась в каскад
 * тернарников: доступ к заданию (закрыто / нет права / чужое), строки без
 * резерва и оформленная недостача.
 *
 * Порядок важен: сначала «можно ли вообще работать», потом «что не так с
 * товаром» — кладовщик читает сверху вниз и останавливается на первом ответе.
 */

import { ExternalLink } from "lucide-react";
import { Link } from "react-router";

import { PickingTaskAccessNotice } from "./PickingTaskAccessNotice";
import { formatUnlinkedNotice } from "./picking-item-texts";
import { Button } from "../../ui/button";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { pluralRu } from "../../../lib/plural";
import { ORDER_ITEM_UNLINKED_TEXT } from "../../../lib/order-item-link";
import type { PickingTaskDetailDto } from "../../../lib/api-picking";

interface Props {
  task: PickingTaskDetailDto;
  /** Право SHOP_WMS_PICKING. */
  canExecute: boolean;
  /** Задание взял текущий пользователь. */
  mine: boolean;
  /** Слаг магазина из URL — без него ссылку на заказ не строим. */
  slug?: string;
  /** Право SHOP_VIEW_ORDERS: кладовщику без него ссылка не положена. */
  canOpenOrder?: boolean;
}

export function PickingTaskNotices({
  task,
  canExecute,
  mine,
  slug,
  canOpenOrder = false,
}: Props) {
  const withoutReservation = task.itemsWithoutReservation ?? 0;
  const unlinked = task.unlinkedItems;
  return (
    <>
      <PickingTaskAccessNotice task={task} canExecute={canExecute} mine={mine} />

      {/* Позиция заказа без фасовки (OQ-50): чинится правкой ЗАКАЗА, сервер
          такое задание провести не даст (409 `picking_item_unlinked`). */}
      {unlinked.length > 0 ? (
        <NoticeStrip
          tone="error"
          // На 360px длинная ссылка не влезает рядом с текстом — переносим её
          // на свою строку, а не режем подпись.
          className="flex-wrap"
          // Чинится во вкладке «Позиции» заказа (OQ-54) — ведём прямо туда, а
          // не оставляем «исправьте заказ» без адреса.
          action={
            canOpenOrder && slug ? (
              <Button variant="outline" className="min-h-12 w-full sm:w-auto" asChild>
                <Link to={`/shop/${slug}/orders/${task.orderId}?tab=items`}>
                  <ExternalLink className="size-4" aria-hidden />
                  {ORDER_ITEM_UNLINKED_TEXT.openOrderItems}
                </Link>
              </Button>
            ) : undefined
          }
        >
          {formatUnlinkedNotice(unlinked)}
        </NoticeStrip>
      ) : null}

      {/* Позиции без брони: остаток под них займётся только при проведении, и
          между сборкой и подписью его может забрать другой заказ. */}
      {withoutReservation > 0 ? (
        <NoticeStrip tone="warning">
          {withoutReservation}{" "}
          {pluralRu(withoutReservation, ["позиция", "позиции", "позиций"])} без
          резерва: товар спишется в момент проведения сборки.
        </NoticeStrip>
      ) : null}

      {task.shortageNote ? (
        <NoticeStrip tone="error">Недостача: {task.shortageNote}</NoticeStrip>
      ) : null}
    </>
  );
}
